import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { reportsService, contractorsService, storageService } from "@/services";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Clock,
  User,
  Calendar,
  Tag,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  XCircle,
  Loader2,
  Trash2,
  Mail,
  UserCog,
  Camera,
  X,
  Pencil,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { captureEvent } from "@/lib/analytics";

const statusConfig = {
  reported: { 
    label: 'New', 
    color: 'bg-red-100 text-red-700 border-red-300', 
    icon: AlertCircle 
  },
  acknowledged: { 
    label: 'Acknowledged', 
    color: 'bg-yellow-100 text-yellow-700 border-yellow-300', 
    icon: Clock 
  },
  in_progress: { 
    label: 'In Progress', 
    color: 'bg-blue-100 text-blue-700 border-blue-300', 
    icon: TrendingUp 
  },
  waiting_contractor: { 
    label: 'Waiting on Contractor', 
    color: 'bg-orange-100 text-orange-700 border-orange-300', 
    icon: Clock 
  },
  dependent_on: {
    label: 'Dependant On',
    color: 'bg-purple-100 text-purple-700 border-purple-300',
    icon: Clock
  },
  pending_approval: {
    label: 'Pending Approval',
    color: 'bg-teal-100 text-teal-700 border-teal-300',
    icon: Clock
  },
  resolved: { 
    label: 'Completed', 
    color: 'bg-green-100 text-green-700 border-green-300', 
    icon: CheckCircle2 
  },
  closed: { 
    label: 'Closed', 
    color: 'bg-gray-100 text-gray-700 border-gray-300', 
    icon: XCircle 
  },
};

const categoryLabels = {
  electrical: "Electrical ⚡",
  plumbing: "Plumbing 🔧",
  security: "Security 🔐",
  general_maintenance: "General Maintenance 🛠️",
  garden_landscape: "Garden/Landscape 🌿",
  structural: "Structural 🏗️",
  other: "Other 📋",
};

const assignmentLabels = {
  self: "Self",
  contractor: "Contractor", 
  staff: "Staff",
};

export default function InternalIssueDetail({ issue, isOpen, onClose, subscription }) {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentIssue, setCurrentIssue] = useState(issue);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const photoInputRef = useRef(null);

  useEffect(() => {
    setCurrentIssue(issue);
  }, [issue]);

  const { data: estateInternalIssues = [] } = useQuery({
    queryKey: ["internal-issues-for-dependency", currentIssue?.estate_id],
    queryFn: () => reportsService.filter({ estate_id: currentIssue.estate_id, is_internal: true }, '-created_date'),
    enabled: !!currentIssue?.estate_id && isOpen,
  });

  // Fetch contractors for this estate
  const { data: contractors = [] } = useQuery({
    queryKey: ["contractors", currentIssue?.estate_id],
    queryFn: () => contractorsService.listByEstate(currentIssue.estate_id),
    enabled: !!currentIssue?.estate_id && isOpen,
  });

  // Get assigned contractor details
  const assignedContractor = contractors.find(c => c.id === currentIssue?.contractor_id);
  const dependencyIssue = estateInternalIssues.find(i => i.id === currentIssue?.depends_on_issue_id);
  const availableDependencyIssues = estateInternalIssues.filter(i => i.id !== currentIssue?.id);

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus) => {
      const updateData = { status: newStatus };
      
      // Set resolved_date when completing the issue
      if (newStatus === 'resolved') {
        updateData.resolved_date = new Date().toISOString();
      }

      if (newStatus !== 'dependent_on') {
        updateData.depends_on_issue_id = null;
      }
      
      return await reportsService.update(currentIssue.id, updateData);
    },
    onSuccess: (updatedIssue, newStatus) => {
      setCurrentIssue(updatedIssue);
      captureEvent('internal_issue_status_updated', {
        issue_id: updatedIssue?.id,
        previous_status: currentIssue?.status,
        new_status: newStatus,
      });
      toast.success('Status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['estate-reports'] });
      queryClient.invalidateQueries({ queryKey: ['estate-stats'] });
      queryClient.invalidateQueries({ queryKey: ['internal-issues'] });
      queryClient.invalidateQueries({ queryKey: ['internal-issues-stats'] });
    },
    onError: (error) => {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    },
  });

  // Assign contractor mutation
  const assignContractorMutation = useMutation({
    mutationFn: async (contractorId) => {
      const contractor = contractors.find(c => c.id === contractorId);
      return await reportsService.update(currentIssue.id, { 
        contractor_id: contractorId,
        assigned_to: contractor ? contractor.name : null
      });
    },
    onSuccess: (updatedIssue, contractorId) => {
      setCurrentIssue(updatedIssue);
      const contractor = contractors.find((item) => item.id === contractorId);
      captureEvent('internal_issue_contractor_assigned', {
        issue_id: updatedIssue?.id,
        contractor_id: contractorId,
        contractor_name: contractor?.name,
      });
      toast.success('Contractor assigned successfully');
      queryClient.invalidateQueries({ queryKey: ['internal-issues'] });
      queryClient.invalidateQueries({ queryKey: ['estate-reports'] });
    },
    onError: (error) => {
      console.error('Failed to assign contractor:', error);
      toast.error('Failed to assign contractor');
    },
  });

  const updateDependencyMutation = useMutation({
    mutationFn: async (dependencyIssueId) => {
      const updateData = {
        depends_on_issue_id: dependencyIssueId,
      };

      if (dependencyIssueId) {
        updateData.status = 'dependent_on';
      }

      return await reportsService.update(currentIssue.id, updateData);
    },
    onSuccess: (updatedIssue, dependencyIssueId) => {
      setCurrentIssue(updatedIssue);
      const selectedDependency = estateInternalIssues.find((item) => item.id === dependencyIssueId);
      captureEvent('internal_issue_dependency_updated', {
        issue_id: updatedIssue?.id,
        depends_on_issue_id: dependencyIssueId || null,
        depends_on_issue_title: selectedDependency?.title,
      });
      toast.success('Dependency updated successfully');
      queryClient.invalidateQueries({ queryKey: ['internal-issues'] });
      queryClient.invalidateQueries({ queryKey: ['estate-reports'] });
      queryClient.invalidateQueries({ queryKey: ['internal-issues-for-dependency'] });
    },
    onError: (error) => {
      console.error('Failed to update dependency:', error);
      toast.error('Failed to update dependency');
    },
  });

  const updateDescriptionMutation = useMutation({
    mutationFn: async (newDescription) => {
      return await reportsService.update(currentIssue.id, { description: newDescription });
    },
    onSuccess: (updatedIssue) => {
      setCurrentIssue(updatedIssue);
      setIsEditingDescription(false);
      captureEvent('internal_issue_description_updated', { issue_id: updatedIssue?.id });
      toast.success('Description updated successfully');
      queryClient.invalidateQueries({ queryKey: ['internal-issues'] });
      queryClient.invalidateQueries({ queryKey: ['estate-reports'] });
    },
    onError: (error) => {
      console.error('Failed to update description:', error);
      toast.error('Failed to update description');
    },
  });

  const updatePhotosMutation = useMutation({
    mutationFn: async (photoUrls) => {
      return await reportsService.update(currentIssue.id, {
        photo_urls: photoUrls,
      });
    },
    onSuccess: (updatedIssue, photoUrls) => {
      setCurrentIssue(updatedIssue);
      captureEvent('internal_issue_photos_updated', {
        issue_id: updatedIssue?.id,
        photos_count: photoUrls?.length || 0,
      });
      toast.success('Photos updated successfully');
      queryClient.invalidateQueries({ queryKey: ['internal-issues'] });
      queryClient.invalidateQueries({ queryKey: ['estate-reports'] });
    },
    onError: (error) => {
      console.error('Failed to update photos:', error);
      toast.error('Failed to update photos');
    },
  });

  // Delete mutation
  const deleteIssueMutation = useMutation({
    mutationFn: async () => {
      return await reportsService.delete(currentIssue.id);
    },
    onSuccess: async () => {
      const deletedIssueId = currentIssue?.id;

      queryClient.setQueriesData({ queryKey: ['internal-issues'] }, (previous) => {
        if (!Array.isArray(previous)) return previous;
        return previous.filter((issue) => issue.id !== deletedIssueId);
      });

      queryClient.setQueriesData({ queryKey: ['estate-reports'] }, (previous) => {
        if (!Array.isArray(previous)) return previous;
        return previous.filter((issue) => issue.id !== deletedIssueId);
      });

      captureEvent('internal_issue_deleted', {
        issue_id: currentIssue?.id,
        category: currentIssue?.category,
      });
      toast.success('Internal issue deleted successfully');

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['estate-reports'] }),
        queryClient.invalidateQueries({ queryKey: ['estate-stats'] }),
        queryClient.invalidateQueries({ queryKey: ['internal-issues'] }),
        queryClient.invalidateQueries({ queryKey: ['internal-issues-stats'] }),
      ]);

      onClose();
    },
    onError: (error) => {
      console.error('Failed to delete issue:', error);
      toast.error(error?.message || 'Failed to delete issue');
    },
  });

  const handleStatusChange = (newStatus) => {
    if (!subscription?.hasWriteAccess) {
      toast.error("Subscription required", {
        description: "Upgrade to update issue status"
      });
      return;
    }

    setCurrentIssue(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        status: newStatus,
        ...(newStatus !== 'dependent_on' ? { depends_on_issue_id: null } : {})
      };
    });

    setIsUpdating(true);
    updateStatusMutation.mutate(newStatus);
    setTimeout(() => setIsUpdating(false), 500);
  };

  const handleDelete = () => {
    if (!subscription?.hasWriteAccess) {
      toast.error("Subscription required", {
        description: "Upgrade to delete issues"
      });
      return;
    }
    deleteIssueMutation.mutate();
    setShowDeleteConfirm(false);
  };

  const handleAddPhotos = async (event) => {
    if (!subscription?.hasWriteAccess) {
      toast.error("Subscription required", {
        description: "Upgrade to add photos"
      });
      return;
    }

    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const existingPhotos = currentIssue.photo_urls || [];
    if (existingPhotos.length + files.length > 5) {
      toast.error('Maximum 5 photos allowed per issue');
      event.target.value = '';
      return;
    }

    setIsUploadingPhotos(true);
    try {
      const uploadedPhotos = await storageService.uploadMultiple(files, 'report-photos');
      const updatedPhotos = [...existingPhotos, ...uploadedPhotos.map((photo) => photo.file_url)];
      await updatePhotosMutation.mutateAsync(updatedPhotos);
    } catch (error) {
      console.error('Photo upload failed:', error);
      toast.error('Failed to upload photos');
    } finally {
      setIsUploadingPhotos(false);
      event.target.value = '';
    }
  };

  const handleRemovePhoto = async (photoUrlToRemove) => {
    if (!subscription?.hasWriteAccess) {
      toast.error("Subscription required", {
        description: "Upgrade to remove photos"
      });
      return;
    }

    const confirmed = window.confirm("Remove this photo from the issue?");
    if (!confirmed) return;

    const existingPhotos = currentIssue.photo_urls || [];
    const updatedPhotos = existingPhotos.filter((url) => url !== photoUrlToRemove);
    updatePhotosMutation.mutate(updatedPhotos);
  };

  if (!currentIssue) return null;

  const statusInfo = statusConfig[currentIssue.status] || statusConfig.reported;
  const StatusIcon = statusInfo.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-slate-600" />
                {currentIssue.title}
              </DialogTitle>
              <DialogDescription className="mt-2">
                Manage and track internal estate maintenance issue details and status updates.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={statusInfo.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {currentIssue.status === 'dependent_on' && dependencyIssue
                  ? `Dependant On: ${dependencyIssue.title}`
                  : statusInfo.label}
              </Badge>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (!subscription?.hasWriteAccess) {
                            toast.error("Subscribe to delete issues");
                            return;
                          }
                          setShowDeleteConfirm(true);
                        }}
                        disabled={!subscription?.hasWriteAccess || deleteIssueMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete issue"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {!subscription?.hasWriteAccess && (
                    <TooltipContent>
                      <p>Subscribe to delete issues</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Issue Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Tag className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">Category:</span>
                  <span>{categoryLabels[currentIssue.category] || currentIssue.category}</span>
                </div>

                {dependencyIssue && (
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="w-4 h-4 text-slate-500" />
                    <span className="font-medium">Dependent on:</span>
                    <span>{dependencyIssue.title}</span>
                  </div>
                )}
                
                {currentIssue.assigned_to && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="font-medium">Assigned to:</span>
                    <span>{assignmentLabels[currentIssue.assigned_to] || currentIssue.assigned_to}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">Created:</span>
                  <span>{format(new Date(currentIssue.created_date), 'PPP')}</span>
                </div>
                
                {currentIssue.resolved_date && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="font-medium">Completed:</span>
                    <span>{format(new Date(currentIssue.resolved_date), 'PPP')}</span>
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <TrendingUp className="w-4 h-4 text-slate-500" />
                  Update Status
                </div>
                <Select 
                  value={currentIssue.status}
                  onValueChange={handleStatusChange}
                  disabled={!subscription?.hasWriteAccess || isUpdating || updateStatusMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reported">New</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_contractor">Waiting on Contractor</SelectItem>
                    <SelectItem value="dependent_on">Dependant On</SelectItem>
                    <SelectItem value="pending_approval">Pending Approval</SelectItem>
                    <SelectItem value="resolved">Completed</SelectItem>
                  </SelectContent>
                </Select>

                {(currentIssue.status === 'dependent_on' || currentIssue.depends_on_issue_id) && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Tag className="w-4 h-4 text-slate-500" />
                      Select Dependency Issue
                    </div>
                    <Select
                      value={currentIssue.depends_on_issue_id || "none"}
                      onValueChange={(value) => {
                        if (!subscription?.hasWriteAccess) {
                          toast.error("Subscribe to set dependencies");
                          return;
                        }
                        setCurrentIssue(prev => {
                          if (!prev) return prev;
                          return {
                            ...prev,
                            depends_on_issue_id: value === "none" ? null : value,
                            ...(value === "none" ? {} : { status: 'dependent_on' })
                          };
                        });
                        updateDependencyMutation.mutate(value === "none" ? null : value);
                      }}
                      disabled={!subscription?.hasWriteAccess || updateDependencyMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select issue this depends on" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No dependency</SelectItem>
                        {availableDependencyIssues.map((dependency) => (
                          <SelectItem key={dependency.id} value={dependency.id}>
                            {dependency.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Contractor Assignment */}
                {contractors.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <UserCog className="w-4 h-4 text-slate-500" />
                      Assign Contractor
                    </div>
                    <Select 
                      value={currentIssue.contractor_id || "none"} 
                      onValueChange={(value) => {
                        if (!subscription?.hasWriteAccess) {
                          toast.error("Subscribe to assign contractors");
                          return;
                        }
                        assignContractorMutation.mutate(value === "none" ? null : value);
                      }}
                      disabled={!subscription?.hasWriteAccess || assignContractorMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select contractor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No contractor assigned</SelectItem>
                        {contractors.map((contractor) => (
                          <SelectItem key={contractor.id} value={contractor.id}>
                            {contractor.name}{contractor.company ? ` (${contractor.company})` : ''} - {contractor.specialty}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {assignedContractor && (
                      <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg space-y-2">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">
                          {assignedContractor.name}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">
                          {assignedContractor.specialty}
                        </div>
                        <div className="flex gap-2">
                          {assignedContractor.email && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const subject = `Internal Issue: ${currentIssue.title}`;
                                const body = 
                                  `Hi ${assignedContractor.name},\n\n` +
                                  `You have been assigned to an internal issue:\n\n` +
                                  `Title: ${currentIssue.title}\n` +
                                  `Category: ${categoryLabels[currentIssue.category] || currentIssue.category}\n` +
                                  `Status: ${statusConfig[currentIssue.status]?.label || currentIssue.status}\n\n` +
                                  `Description:\n${currentIssue.description}\n\n` +
                                  `Please review and take necessary action.\n\n` +
                                  `Thank you`;
                                
                                const mailtoLink = `mailto:${assignedContractor.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                                
                                // Detect if Windows (likely no email client)
                                const isWindows = navigator.platform.toLowerCase().includes('win');
                                
                                if (isWindows) {
                                  // Show options for Windows users
                                  const choice = confirm(
                                    `Send email to ${assignedContractor.name}?\n\n` +
                                    `Click OK to open Gmail\n` +
                                    `Click Cancel to open Outlook/default email client`
                                  );
                                  
                                  if (choice) {
                                    // Open Gmail compose in new tab
                                    const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${assignedContractor.email}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                                    window.open(gmailLink, '_blank');
                                  } else {
                                    // Try Outlook web or mailto fallback
                                    const outlookLink = `https://outlook.live.com/mail/0/deeplink/compose?to=${assignedContractor.email}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                                    
                                    // Try mailto first (catches Outlook desktop app)
                                    const mailtoWindow = window.open(mailtoLink, '_blank');
                                    
                                    // If mailto doesn't work, open Outlook web after short delay
                                    setTimeout(() => {
                                      if (!mailtoWindow || mailtoWindow.closed) {
                                        window.open(outlookLink, '_blank');
                                      }
                                    }, 1000);
                                  }
                                } else {
                                  // Use mailto for Mac/Linux/Mobile (native email apps)
                                  window.location.href = mailtoLink;
                                }
                              }}
                              className="text-orange-600 border-orange-200 hover:bg-orange-50"
                            >
                              <Mail className="w-3 h-3 mr-1" />
                              Email Contractor
                            </Button>
                          )}
                          {assignedContractor.phone && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.location.href = `tel:${assignedContractor.phone}`}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              Call
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {(isUpdating || updateStatusMutation.isPending || updateDependencyMutation.isPending || updatePhotosMutation.isPending || isUploadingPhotos) && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Updating...
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Description */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Description</h3>
              {!isEditingDescription && subscription?.hasWriteAccess && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDescriptionDraft(currentIssue.description || '');
                    setIsEditingDescription(true);
                  }}
                  className="h-7 px-2 text-slate-500 hover:text-slate-700"
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            {isEditingDescription ? (
              <div className="space-y-2">
                <Textarea
                  value={descriptionDraft}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                  rows={5}
                  className="resize-none text-sm"
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditingDescription(false)}
                    disabled={updateDescriptionMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => updateDescriptionMutation.mutate(descriptionDraft)}
                    disabled={updateDescriptionMutation.isPending || descriptionDraft.trim() === ''}
                  >
                    {updateDescriptionMutation.isPending ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Saving...</>
                    ) : 'Save'}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-slate-600 whitespace-pre-wrap">{currentIssue.description}</p>
            )}
          </Card>

          {/* Photos */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Photos</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  {(currentIssue.photo_urls || []).length}/5
                </span>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleAddPhotos}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={!subscription?.hasWriteAccess || isUploadingPhotos || updatePhotosMutation.isPending || (currentIssue.photo_urls || []).length >= 5}
                >
                  {isUploadingPhotos ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Camera className="w-3 h-3 mr-1" />
                  )}
                  Add Photos
                </Button>
              </div>
            </div>

            {currentIssue.photo_urls && currentIssue.photo_urls.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {currentIssue.photo_urls.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Issue photo ${index + 1}`}
                      className="w-full h-48 object-cover rounded-lg border hover:opacity-90 transition-opacity cursor-pointer"
                      onClick={() => window.open(url, '_blank')}
                    />
                    {subscription?.hasWriteAccess && (
                      <Button
                        size="icon"
                        variant="destructive"
                        className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleRemovePhoto(url)}
                        disabled={updatePhotosMutation.isPending || isUploadingPhotos}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No photos added yet.</p>
            )}
          </Card>

          {/* Activity Timeline */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Activity Timeline</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-slate-500">
                  {format(new Date(currentIssue.created_date), 'PPp')}
                </span>
                <span>Issue created</span>
              </div>
              
              {currentIssue.status !== 'reported' && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-slate-500">
                    {format(new Date(currentIssue.updated_at || currentIssue.created_date), 'PPp')}
                  </span>
                  <span>Status updated to {statusInfo.label}</span>
                </div>
              )}
              
              {currentIssue.resolved_date && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-slate-500">
                    {format(new Date(currentIssue.resolved_date), 'PPp')}
                  </span>
                  <span>Issue completed</span>
                </div>
              )}
            </div>
          </Card>

          {/* Footer */}
          <div className="flex justify-end pt-4">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Internal Issue?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{currentIssue.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              disabled={deleteIssueMutation.isPending}
            >
              {deleteIssueMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}